import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Check, AlertCircle, ChevronDown, ChevronUp, RefreshCw, History } from 'lucide-react';
import { uploadManager } from '@/lib/uploadManager';
import { queueStats } from '@/lib/upload';
import { useUploadManagerState } from '@/hooks/useUploadManager';
import { usePodcastPlayer } from '@/components/podcast/PodcastPlayerContext';
import { useToast } from '@/components/Toast';
import UploadStrip from './UploadStrip';
import InterruptedUploads, { interruptedTitle } from './InterruptedUploads';

/**
 * Upload progress on every page, mounted once at the root next to the podcast
 * player. Sits at the inline-start corner, opposite the toast stack (end side),
 * so a toast never lands on top of it — in RTL too. Uploads used to be visible only inside the Library, so leaving it
 * looked like they had stopped. Steps aside while a page shows its own inline
 * strip (the Library), so the same queue is never drawn twice.
 */
export default function GlobalUploadPanel() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const state = useUploadManagerState();
  const podcast = usePodcastPlayer();
  // Starts folded on a phone, where the open panel would cover half the page.
  const [collapsed, setCollapsed] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  const warned = useRef(false);

  // Uploads a reload cut short. Once per app load; the Library refreshes again
  // when it opens.
  useEffect(() => { void uploadManager.refreshInterrupted(); }, []);

  useEffect(() => {
    if (!state.reloadDeferred || warned.current) return;
    warned.current = true;
    toast(t('upload.reloadDeferred', 'A new version of Soltik is ready. The page will not reload while your uploads are running — reload once they finish.'), 'warning', 9000);
  }, [state.reloadDeferred, t, toast]);

  const inline = state.inlineViews > 0;
  const hasItems = !inline && state.items.length > 0;
  const hasInterrupted = !inline && state.interrupted.length > 0;
  const offerReload = state.reloadDeferred && !state.active;
  if (!hasItems && !hasInterrupted && !offerReload) return null;
  if (typeof document === 'undefined') return null;

  const s = queueStats(state.items);
  const lifted = !!(podcast.isVisible && podcast.episode);

  const summary = hasItems
    ? (s.settled
      ? t('upload.finished', 'Uploaded {done} of {total}', { done: s.done, total: s.total })
      : t('upload.inProgress', 'Uploading {done}/{total}', { done: s.done, total: s.total }))
    : hasInterrupted
      ? interruptedTitle(t, state.interrupted)
      : t('upload.panel.updateReady', 'Update ready');

  return createPortal(
    <div
      className={`fixed start-4 ${lifted ? 'bottom-28' : 'bottom-4'} z-9998 w-[min(380px,calc(100vw-2rem))] rounded-xl border border-amber-500/30 bg-[#16161e]/95 backdrop-blur-sm shadow-2xl text-white overflow-hidden`}
      role="region"
      aria-label={t('upload.panel.label', 'Uploads')}
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={!collapsed}
        className="w-full flex items-center gap-2 px-3 py-2 bg-amber-500/10 hover:bg-amber-500/15 cursor-pointer text-start"
      >
        {hasItems
          ? (s.settled
            ? (s.failed > 0 ? <AlertCircle size={14} className="text-red-300 shrink-0" /> : <Check size={14} className="text-emerald-300 shrink-0" />)
            : <Loader2 size={14} className="text-amber-300 animate-spin shrink-0" />)
          : hasInterrupted
            ? <History size={14} className="text-sky-300 shrink-0" />
            : <RefreshCw size={14} className="text-amber-300 shrink-0" />}
        <span className="text-xs font-medium text-amber-100 flex-1 min-w-0 truncate">
          {summary}
          {hasItems && s.totalBytes > 0 && <span className="text-amber-200/50 ms-2 tabular-nums">{s.pct}%</span>}
        </span>
        {collapsed ? <ChevronUp size={14} className="text-amber-200/70 shrink-0" /> : <ChevronDown size={14} className="text-amber-200/70 shrink-0" />}
      </button>
      {hasItems && !s.settled && (
        <div className="h-0.5 bg-amber-500/15">
          <div className="h-full bg-amber-400 transition-all duration-300" style={{ width: `${s.pct}%` }} />
        </div>
      )}

      {!collapsed && (
        <div className="max-h-[60vh] overflow-y-auto">
          {hasItems && (
            <UploadStrip
              variant="panel"
              items={state.items}
              onCancel={(id) => uploadManager.cancel(id)}
              onCancelAll={() => uploadManager.cancelAll()}
              onRetryFailed={() => uploadManager.retryFailed()}
              onRetryOne={(id) => uploadManager.retryOne(id)}
              onDismissErrors={() => uploadManager.clearErrors()}
              maxRows={5}
            />
          )}
          {hasItems && state.active && (
            <div className="px-3 pb-2 text-[10px] text-white/40">
              {t('upload.panel.keepOpen', 'Keep this tab open until uploads finish. You can move between pages freely.')}
            </div>
          )}
          {hasInterrupted && <InterruptedUploads variant="panel" showTitle={hasItems} />}
          {offerReload && (
            <div className="px-3 py-2 border-t border-white/8 flex items-center gap-2 text-[11px] text-amber-100/80">
              <span className="flex-1">{t('upload.panel.reloadNow', 'A new version is ready.')}</span>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-1 rounded-md bg-amber-400 px-2 py-1 font-medium text-black hover:bg-amber-300 cursor-pointer"
              >
                <RefreshCw size={11} /> {t('upload.panel.reload', 'Reload')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>,
    document.body,
  );
}
